import { Colors } from '@amelio/ui-library';
import { motion } from 'framer-motion';
import { createContext, useContext, useRef, type ReactNode } from 'react';

import { usePrefersReducedMotion } from '@/hooks/utils/usePrefersReducedMotion';

/** One crossing of the highlight, from a bone's leading edge to its trailing one. */
const SHIMMER_DURATION_S = 1.6;

/**
 * How far behind its predecessor a bone's crossing runs, wrapped into one crossing so that a
 * placeholder made of many bones never leaves its last ones still while the first ones move.
 */
const SHIMMER_STAGGER_S = 0.12;

/** How much of its own colour a bone keeps where the band crosses it; the rest lets the surface through. */
const SHIMMER_BAND_OPACITY = '10%';

/**
 * The band that travels across a bone: the bone's own colour, thinned to almost nothing so that
 * the surface behind shows through it. One declaration, so a bone of any colour lights up the
 * same way, whatever it is laid on.
 */
const shimmerBand = (color: string) =>
    `linear-gradient(100deg, ${color} 35%, color-mix(in srgb, ${color} ${SHIMMER_BAND_OPACITY}, transparent) 50%, ${color} 65%)`;

/** Three times the bone's width, so the band starts and ends clear of both its edges. */
const SHIMMER_TRAVEL = '300% 100%';

interface SkeletonContextValue {
    isAnimating: boolean;
    getBoneIndex: () => number;
}

const SkeletonContext = createContext<SkeletonContextValue | null>(null);

interface SkeletonProps {
    children: ReactNode;
    className?: string;
}

interface BoneProps {
    style?: React.CSSProperties;
    className?: string;
    color?: string;
    dataTestId?: string;
}

const Skeleton = ({ children, className = '' }: SkeletonProps) => {
    const boneIndexRef = useRef(0);

    const getBoneIndex = () => {
        const currentIndex = boneIndexRef.current;
        boneIndexRef.current += 1;
        return currentIndex;
    };

    return (
        <SkeletonContext.Provider value={{ isAnimating: true, getBoneIndex }}>
            <div className={className}>{children}</div>
        </SkeletonContext.Provider>
    );
};

const Bone = ({ style, className = '', color = Colors.WHITE, dataTestId }: BoneProps) => {
    const context = useContext(SkeletonContext);

    if (!context) {
        throw new Error('Bone must be used within a Skeleton component');
    }

    const hasCustomRounded = /\brounded-[a-z0-9]+\b/.test(className);
    const baseClassName = hasCustomRounded ? '' : 'rounded-lg';

    const boneIndex = useRef(context.getBoneIndex()).current;
    const prefersReducedMotion = usePrefersReducedMotion();

    // Asked to cut down on movement, a bone is its resting colour and nothing else: no band to
    // paint, so nothing left to move across it.
    if (prefersReducedMotion) {
        return (
            <div
                className={`${baseClassName} ${className}`}
                style={{ backgroundColor: color, ...style }}
                data-testid={dataTestId}
            />
        );
    }

    return (
        <motion.div
            className={`${baseClassName} ${className}`}
            data-testid={dataTestId}
            // The band carries the resting colour itself: a fill underneath it would show through
            // its thinned middle and the crossing would never be seen.
            style={{
                backgroundImage: shimmerBand(color),
                backgroundSize: SHIMMER_TRAVEL,
                ...style,
            }}
            animate={{ backgroundPosition: ['100% 0', '0% 0'] }}
            transition={{
                duration: SHIMMER_DURATION_S,
                repeat: Infinity,
                ease: 'linear',
                delay: (boneIndex * SHIMMER_STAGGER_S) % SHIMMER_DURATION_S,
            }}
        />
    );
};

export { Skeleton, Bone };
